import { createContext, useCallback, useContext, useMemo } from "react";

import type { I18nT } from "@lib/dashboardI18n";
import { exhaustiveCheck } from "@lib/exhaustiveCheck";

import { addComponentStoreToLiquidScanFields } from "./addComponentStoreToLiquidScanFields";
import { defaultContentOptimizationContextStruct, minimumComponentValuesForActive } from "./constants";
import type { ContentOptimizationComponentName, ContentOptimizationContextStruct } from "./types";
import { isContentOptimizationComponentName } from "./types";

export const ContentOptimizationContext = createContext<ContentOptimizationContextStruct>(
  defaultContentOptimizationContextStruct
);
export const useContentOptimizationContext = () => useContext(ContentOptimizationContext);

export const useContentOptimizationLiquidScanFields = (baseLiquidFields: string[]): string[] => {
  const { isContentOptimization, componentStoreData } = useContentOptimizationContext();

  return useMemo(
    () => addComponentStoreToLiquidScanFields(baseLiquidFields, isContentOptimization, componentStoreData),
    [baseLiquidFields, isContentOptimization, componentStoreData]
  );
};

export const useContentOptimizationActiveComponentNames = () => {
  const contentOptimizationContext = useContentOptimizationContext();

  const components = contentOptimizationContext?.componentStoreData?.components || {};
  const activeComponentNames = Object.entries(components).reduce((acc, pair) => {
    const [name, values] = pair;

    // the default is many empty strings, so we must be defensive here
    const nonEmptyValueCount = (values || []).reduce((acc2, value) => {
      return acc2 + (value && value != "" ? 1 : 0);
    }, 0);

    if (nonEmptyValueCount >= minimumComponentValuesForActive) {
      acc.push(name);
    }
    return acc;
  }, [] as string[]);

  return activeComponentNames;
};

export const useComponentNameLocalizer = (t: I18nT<"content-optimization">) => {
  const localizeStandardComponentName = useCallback(
    (componentName: ContentOptimizationComponentName) => {
      switch (componentName) {
        case "Sender Name":
          return t("sender-name", "Sender Name");
        case "Subject":
          return t("subject", "Subject");
        case "Preheader":
          return t("preheader", "Preheader");
        case "Body Header":
          return t("body-header", "Body Header");
        case "Body Content":
          return t("body-content", "Body Content");
        case "Primary CTA":
          return t("primary-call-to-action-abbreviated", "Primary CTA");
        case "Image":
          return t("image", "Image");
        case "Title":
          return t("title", "Title");
        case "Message":
          return t("message", "Message");
        case "Hook":
          return t("hook", "Hook");
        case "Body":
          return t("body", "Body");
        case "CTA":
          return t("cta", "CTA");
        default:
          exhaustiveCheck(componentName);
      }
    },
    [t]
  );

  return useCallback(
    (componentName: string) => {
      // check the type so that we fail CI if we add a new value but forget to add support
      return isContentOptimizationComponentName(componentName)
        ? localizeStandardComponentName(componentName)
        : componentName;
    },
    [localizeStandardComponentName]
  );
};
